/**
 * Stochastic RSI (StochRSI)
 *
 * Applies the Stochastic oscillator formula to RSI values instead of price.
 * More sensitive than standard RSI, providing earlier signals.
 *
 * Category: Momentum | Pane: Separate
 */
import { IndicatorDefinition, PlotData } from "../types/indicator.types";
import { CandleData } from "../../types/chart.types";
import { getSource } from "./sma";

export const stochRsiIndicator: IndicatorDefinition = {
  id: "stochrsi",
  name: "Stochastic RSI",
  shortName: "StochRSI",
  category: "momentum",
  pane: "separate",
  params: [
    { key: "rsiLength", label: "RSI Length", type: "number", default: 14, min: 2, max: 200 },
    { key: "stochLength", label: "Stoch Length", type: "number", default: 14, min: 2, max: 200 },
    { key: "kSmooth", label: "%K Smooth", type: "number", default: 3, min: 1, max: 50 },
    { key: "dSmooth", label: "%D Smooth", type: "number", default: 3, min: 1, max: 50 },
  ],
  plots: [
    { key: "k", label: "%K", color: "#2196F3", style: "line", lineWidth: 2, priceScaleId: "stochrsi" },
    { key: "d", label: "%D", color: "#FF9800", style: "line", lineWidth: 1, priceScaleId: "stochrsi" },
  ],
  calculate: (candles: CandleData[], params: Record<string, any>): PlotData[] => {
    const { rsiLength, stochLength, kSmooth, dSmooth } = params;
    const src = getSource(candles, "close");

    // Calculate RSI values first
    const rsiValues: number[] = [];
    if (src.length < rsiLength + 1) return [{ key: "k", data: [] }, { key: "d", data: [] }];

    let avgGain = 0, avgLoss = 0;
    for (let i = 1; i <= rsiLength; i++) {
      const change = src[i] - src[i - 1];
      if (change > 0) avgGain += change; else avgLoss += Math.abs(change);
    }
    avgGain /= rsiLength;
    avgLoss /= rsiLength;

    for (let i = 0; i < rsiLength; i++) rsiValues.push(NaN);
    rsiValues.push(avgLoss === 0 ? 100 : 100 - 100 / (1 + avgGain / avgLoss));

    for (let i = rsiLength + 1; i < src.length; i++) {
      const change = src[i] - src[i - 1];
      avgGain = (avgGain * (rsiLength - 1) + (change > 0 ? change : 0)) / rsiLength;
      avgLoss = (avgLoss * (rsiLength - 1) + (change < 0 ? Math.abs(change) : 0)) / rsiLength;
      rsiValues.push(avgLoss === 0 ? 100 : 100 - 100 / (1 + avgGain / avgLoss));
    }

    // Apply stochastic to RSI
    const stochRsi: number[] = [];
    for (let i = 0; i < rsiValues.length; i++) {
      if (i < rsiLength + stochLength - 1 || isNaN(rsiValues[i])) {
        stochRsi.push(NaN);
        continue;
      }
      let highest = -Infinity, lowest = Infinity;
      for (let j = i - stochLength + 1; j <= i; j++) {
        if (!isNaN(rsiValues[j])) {
          if (rsiValues[j] > highest) highest = rsiValues[j];
          if (rsiValues[j] < lowest) lowest = rsiValues[j];
        }
      }
      stochRsi.push(highest === lowest ? 50 : ((rsiValues[i] - lowest) / (highest - lowest)) * 100);
    }

    // SMA smooth for %K
    const kData: number[] = [];
    for (let i = 0; i < stochRsi.length; i++) {
      if (isNaN(stochRsi[i]) || i < rsiLength + stochLength + kSmooth - 3) {
        kData.push(NaN);
        continue;
      }
      let sum = 0, count = 0;
      for (let j = i - kSmooth + 1; j <= i; j++) {
        if (!isNaN(stochRsi[j])) { sum += stochRsi[j]; count++; }
      }
      kData.push(count > 0 ? sum / count : NaN);
    }

    // SMA smooth for %D
    const kOut: { time: number; value: number }[] = [];
    const dOut: { time: number; value: number }[] = [];

    for (let i = 0; i < kData.length; i++) {
      if (!isNaN(kData[i])) {
        kOut.push({ time: candles[i].time, value: kData[i] });
      }
      if (i >= dSmooth - 1) {
        let sum = 0, count = 0;
        for (let j = i - dSmooth + 1; j <= i; j++) {
          if (!isNaN(kData[j])) { sum += kData[j]; count++; }
        }
        if (count > 0) dOut.push({ time: candles[i].time, value: sum / count });
      }
    }

    return [{ key: "k", data: kOut }, { key: "d", data: dOut }];
  },
};
